<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Read the following article from crimemuseum.org. It explains the role of the forensic
      photographer in crime scene investigation. While a photographer’s role is different from that
      of a forensic scientist, forensic scientists often take and use photographs of crime scenes in
      their work. Therefore, it is worth spending some time learning more about this area of crime
      scene investigation.
    </p>
    <p class="mb-5">
      Crime scene photography or <strong>forensic photography</strong> has been around almost as
      long as the camera. Television has altered the way that people typically think of forensic
      photography featuring several crime shows with swarms of photographers appearing at the crime
      scene. Since the main goal of forensic photography is to capture evidence that is admissible
      in court, having many photographers at the scene would not be ideal. A forensic photographer
      must photograph everything in case it will need to be used as evidence in court. All the
      photography must take place before evidence has had the chance to be disturbed.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          The first use of forensic photography was in the nineteenth century by
          <strong>Alphonse Bertillon</strong>. This makes him the first forensic photographer. It is
          said that Bertillon was the first to approach a crime scene like an investigator.
          Bertillon took various shots at the crime scene; some shots were at a distance, while
          others were close up. Some shots were at ground level while others were shot from
          overhead. Today, forensic photography is used to help prosecute a crime because
          photography can capture things such as evidence that can’t physically be collected. Photos
          are also useful to capture evidence that possesses time restraints. For example, the shape
          of a bloodstain will often change over time. Forensic photography also allows
          investigators to see the placement of objects in relation to one another, and the
          placement of the objects around the room to be captured.
        </p>
      </div>
      <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/forensic_science/photography.jpg"
          alt="A forensic investigator in protective clothing photographing evidence on a kitchen floor, with numbered evidence markers beside each item."
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>
    <p class="mb-5">
      There are many things that a forensic photographer can photograph. A few examples include the
      body of the victim, shell casings, or broken glass. A forensic photographer might also be
      asked to photograph injuries on a victim who is alive and was attacked.
    </p>
    <p class="mb-5">
      Those who are interested in forensic photography often have a good eye for detail and take
      great care in their work. A forensic photographer must be methodical in their line of work. A
      forensic photographer can’t afford to leave out any evidence or produce photos that could be
      misleading. A forensic photographer must know their equipment and how it works so that when
      needed, special lighting can be used to create a clear image. It is also important for a
      forensic photographer to know the scale of objects at the crime scene; this way, the objects
      will be represented properly and won’t be misleading.
    </p>
    <p class="mb-5">
      Forensic photographers will often start out as either police officers or crime scene
      investigators. This way, when they do obtain a career in forensic photography, they are
      prepared for when they need to present evidence in court. Other forensic photographers will
      start in the lab to get experience and contacts so that they have a better chance of getting
      hired. After gaining some sort of experience, some may opt to take a forensic photography
      course at the college or university level. There are many different places where a forensic
      photographer could get hired, including the military or the arson investigation branch of the
      fire department.
    </p>

    <h4 class="mb-5">Forensic Photography Methods</h4>
    <p class="mb-5">
      To find out more about how to take photographs at a crime scene check, out this very
      informative video:
    </p>
    <responsive-youtube-wrapper :video="videos[1].url" :image="videos[1].image" />
    <p class="mb-5">
      This is the same topic with a slightly different perspective. Note how the presenter stresses
      the importance of taking photos from each room aspect, ie: the four corners.
    </p>
    <responsive-youtube-wrapper :video="videos[2].url" :image="videos[2].image" />
    <h4 class="mb-5">Home Burglary</h4>

    <p class="mb-5">
      Now you have a good understanding of some of the basics of crime scene photography, let’s put
      both your photography skills and your skills of observation to the test.
    </p>
    <ul class="default-list">
      <li>
        First, you’ll need to choose a room in your house to be the crime scene. If you haven’t been
        tidying your bedroom lately, it might serve the purpose well!
      </li>
      <li>
        Next, you are going to take four detailed photographs of this room. Remember to incorporate
        as much of the advice as possible given in the two videos above. Your job is to use your
        camera to capture the crime scene. You need to take overall photos, midrange photos and
        deliberate up front photos.
      </li>
      <li>
        Now, choose another member of your household or a friend to look at the photographs you’ve
        taken. Instruct them to identify four objects that are visible in the photographs to remove
        from the room. They must not tell you which objects they have removed.
      </li>
      <li>
        Finally, can you identify the four objects that were missing by examining your initial
        photographs closely and comparing them to the crime scene?
      </li>
    </ul>
    <p class="mb-5">Record your answers in the table below.</p>

    <table class="table default-table">
      <tr class="subheader">
        <th scope="col" width="5%"></th>
        <th scope="col" width="45%">Item Description</th>
        <th scope="col" width="50%">Item Location</th>
      </tr>
      <tr class="body" v-for="(group, index) in 4" :key="'mg_1_' + index">
        <th scope="row" class="subheader">{{ index + 1 }}</th>
        <td><text-block v-model="data['question_1_1_' + index]" :editable="isEditable" /></td>
        <td><text-block v-model="data['question_1_2_' + index]" :editable="isEditable" /></td>
      </tr>
    </table>
    <p class="mb-5">
      And the last thing you need to do is to upload 2 photos of your “crime scene”. You can upload
      these below:
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Image'"
      index="1"
      filetype="media"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Image'"
      index="2"
      filetype="media"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/forensic_photography_methods_splash.jpg'),
          url: 'https://www.youtube.com/embed/3CCVPa6DtZ8',
        },
        2: {
          image: require('@/assets/images/experiences/forensic_science/crime_scene_photography_splash.jpg'),
          url: 'https://www.youtube.com/embed/qGpGTYPLHVE',
        },
      },
      image1: require('@/assets/images/experiences/forensic_science/smartdraw_button.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
